import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from './AuthContext';

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const card = { width: 340, maxWidth: '92vw' };
const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8, marginTop: 4,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
  color: 'inherit', fontSize: 14, boxSizing: 'border-box',
};
const btnStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8, border: 'none',
  background: '#c49b63', color: '#1a1a1a', fontWeight: 700, fontSize: 14, cursor: 'pointer',
};
const linkBtn = { background: 'none', border: 'none', color: '#4ea1ff', cursor: 'pointer', padding: 0, fontSize: 13 };

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// "Sign in with Google" via Google Identity Services. Loads the GIS script, renders
// Google's button, and on success hands the ID token to the backend (which verifies
// it and mints our session cookie).
function GoogleSignIn({ onError, onClose }) {
  const { loginWithGoogle } = useAuth();
  const ref = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId) { onError('Google sign-in is not configured'); return undefined; }
    let cancelled = false;

    const render = async () => {
      if (cancelled || !window.google?.accounts?.id || !ref.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          const res = await loginWithGoogle(resp.credential);
          if (res.ok) { if (onClose) onClose(); } else { onError(res.error); }
        },
      });
      window.google.accounts.id.renderButton(ref.current, {
        theme: 'filled_black', size: 'large', width: 300, text: 'continue_with',
      });
    };

    if (window.google?.accounts?.id) { render(); return undefined; }
    let script = document.getElementById('gis-script');
    if (!script) {
      script = document.createElement('script');
      script.src = GIS_SRC; script.async = true; script.defer = true; script.id = 'gis-script';
      document.body.appendChild(script);
    }
    script.addEventListener('load', render);
    return () => { cancelled = true; script.removeEventListener('load', render); };
  }, [clientId, loginWithGoogle, onError, onClose]);

  return <div ref={ref} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />;
}

export default function AuthScreen({ onClose }) {
  const { authMode, login, register } = useAuth();
  const isOidc = authMode === 'oidc';
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const res = mode === 'login'
      ? await login(email, password)
      : await register(email, password, displayName);
    setBusy(false);
    if (res.ok) { if (onClose) onClose(); }
    else setError(res.error);
  };

  // Render to document.body via a portal: the sticky header uses backdrop-filter,
  // which would otherwise trap this position:fixed overlay inside the header bar.
  return createPortal(
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="tp-card" style={card}>
        <h2 className="tp-section__title" style={{ marginTop: 0 }}>
          {isOidc ? 'Sign in' : (mode === 'login' ? 'Sign in' : 'Create account')}
        </h2>

        {isOidc ? (
          <>
            <p style={{ marginTop: 0, fontSize: 13, color: 'var(--tp-muted, #9aa4b2)' }}>
              Use your Google account to continue.
            </p>
            <GoogleSignIn onError={setError} onClose={onClose} />
            {error && <div style={{ color: '#e15759', fontSize: 13, marginTop: 10 }}>{error}</div>}
          </>
        ) : (
          <>
            <form onSubmit={submit}>
              {mode === 'register' && (
                <div style={{ marginBottom: 10 }}>
                  <span className="tp-label">Name</span>
                  <input style={inputStyle} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" />
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <span className="tp-label">Email</span>
                <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </div>
              <div style={{ marginBottom: 10 }}>
                <span className="tp-label">Password</span>
                <input style={inputStyle} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
              </div>
              {error && <div style={{ color: '#e15759', fontSize: 13, marginBottom: 8 }}>{error}</div>}
              <button type="submit" style={btnStyle} disabled={busy}>
                {busy ? '…' : (mode === 'login' ? 'Sign in' : 'Create account')}
              </button>
            </form>
            <div style={{ marginTop: 12, fontSize: 13, color: 'var(--tp-muted, #9aa4b2)' }}>
              {mode === 'login'
                ? <>No account? <button type="button" onClick={() => { setMode('register'); setError(''); }} style={linkBtn}>Create one</button></>
                : <>Have an account? <button type="button" onClick={() => { setMode('login'); setError(''); }} style={linkBtn}>Sign in</button></>}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
